import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingBag, ExternalLink, Star, Check, Filter,
  Dumbbell, Wind, Zap, Heart, Shield, Award,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// Equipment Categories
const CATEGORIES = [
  { id: 'all', name: 'All Gear', icon: ShoppingBag },
  { id: 'strength', name: 'Strength', icon: Dumbbell },
  { id: 'speed', name: 'Speed', icon: Wind },
  { id: 'agility', name: 'Agility', icon: Zap },
  { id: 'recovery', name: 'Recovery', icon: Heart },
];

// Equipment Database - affiliateUrl placeholder for future affiliate links
// When you have affiliate links, add them as: affiliateUrl: 'https://amazon.com/dp/...'
const EQUIPMENT = [
  // Strength
  {
    id: 1,
    name: 'Adjustable Dumbbells (5-50 lbs)',
    category: 'strength',
    price: '$299',
    rating: 4.8,
    reviews: 2450,
    image: 'https://images.unsplash.com/photo-1638536532686-d610adfc8e5c?w=400&h=300&fit=crop',
    description: 'Space-saving adjustable dumbbells perfect for home gyms. Quick-change weight system.',
    benefits: ['Space efficient', 'Quick weight changes', 'Replaces 15 sets of weights'],
    tier: 'essential',
    affiliateNote: 'Best for: Home strength training',
    affiliateUrl: null // Add your Amazon/affiliate link here
  },
  {
    id: 2,
    name: 'Olympic Barbell Set',
    category: 'strength',
    price: '$449',
    rating: 4.9,
    reviews: 1890,
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop',
    description: '7ft Olympic barbell with 300lb weight plate set. Chrome finish, knurled grip.',
    benefits: ['Competition grade', 'Durable chrome finish', 'Full weight range'],
    tier: 'advanced',
    affiliateNote: 'Best for: Serious lifters',
    affiliateUrl: null
  },
  {
    id: 3,
    name: 'Resistance Bands Set',
    category: 'strength',
    price: '$35',
    rating: 4.7,
    reviews: 5200,
    image: 'https://images.unsplash.com/photo-1598289431512-b97b0917affc?w=400&h=300&fit=crop',
    description: '5 resistance levels with handles, ankle straps, and door anchor. Travel-friendly.',
    benefits: ['Portable', 'Multiple resistance levels', 'Full body workouts'],
    tier: 'starter',
    affiliateNote: 'Best for: Beginners & travel',
    affiliateUrl: null
  },
  {
    id: 4,
    name: 'Pull-Up Bar (Doorway)',
    category: 'strength',
    price: '$45',
    rating: 4.6,
    reviews: 3100,
    image: 'https://images.unsplash.com/photo-1598971639058-fab3c3109a00?w=400&h=300&fit=crop',
    description: 'No-screw doorway pull-up bar. Multiple grip positions. Holds up to 300 lbs.',
    benefits: ['No installation', 'Multiple grips', 'Compact storage'],
    tier: 'essential',
    affiliateNote: 'Best for: Upper body development',
    affiliateUrl: null
  },
  // Speed
  {
    id: 5,
    name: 'Speed Training Parachute',
    category: 'speed',
    price: '$25',
    rating: 4.5,
    reviews: 1200,
    image: 'https://images.unsplash.com/photo-1461896836934- voices?w=400&h=300&fit=crop',
    description: 'Resistance parachute for sprint training. Adjustable harness fits all sizes.',
    benefits: ['Builds explosive power', 'Adjustable resistance', 'Easy to use'],
    tier: 'essential',
    affiliateNote: 'Best for: Sprint training',
    affiliateUrl: null
  },
  {
    id: 6,
    name: 'Weighted Sled',
    category: 'speed',
    price: '$189',
    rating: 4.8,
    reviews: 890,
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop',
    description: 'Heavy-duty push/pull sled with low friction skis. Loadable up to 500 lbs.',
    benefits: ['Builds power', 'Low impact', 'Versatile training'],
    tier: 'advanced',
    affiliateNote: 'Best for: Power development',
    affiliateUrl: null
  },
  {
    id: 7,
    name: 'Ankle Weights (5lb pair)',
    category: 'speed',
    price: '$28',
    rating: 4.4,
    reviews: 2100,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
    description: 'Adjustable ankle weights with soft neoprene padding. Perfect for speed drills.',
    benefits: ['Comfortable fit', 'Adjustable weight', 'Enhances training'],
    tier: 'starter',
    affiliateNote: 'Best for: Speed & agility drills',
    affiliateUrl: null
  },
  // Agility
  {
    id: 8,
    name: 'Agility Ladder (20ft)',
    category: 'agility',
    price: '$22',
    rating: 4.7,
    reviews: 4500,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
    description: '20-foot speed ladder with 12 rungs. Includes carry bag and drill guide.',
    benefits: ['Improves footwork', 'Portable', 'Multiple drills'],
    tier: 'essential',
    affiliateNote: 'Best for: Footwork & coordination',
    affiliateUrl: null
  },
  {
    id: 9,
    name: 'Training Cones (50 pack)',
    category: 'agility',
    price: '$18',
    rating: 4.6,
    reviews: 3200,
    image: 'https://images.unsplash.com/photo-1461896836934-fffceb64a946?w=400&h=300&fit=crop',
    description: 'Durable plastic training cones in 5 colors. Perfect for drills and marking.',
    benefits: ['Weather resistant', 'High visibility', 'Stackable storage'],
    tier: 'starter',
    affiliateNote: 'Best for: Drill setup',
    affiliateUrl: null
  },
  {
    id: 10,
    name: 'Reaction Ball',
    category: 'agility',
    price: '$12',
    rating: 4.5,
    reviews: 1800,
    image: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&h=300&fit=crop',
    description: '6-sided reaction ball for unpredictable bounces. Improves hand-eye coordination.',
    benefits: ['Random bounces', 'Improves reactions', 'Portable'],
    tier: 'starter',
    affiliateNote: 'Best for: Reaction training',
    affiliateUrl: null
  },
  {
    id: 11,
    name: 'Plyo Box Set (3 heights)',
    category: 'agility',
    price: '$179',
    rating: 4.8,
    reviews: 1450,
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop',
    description: '3-in-1 foam plyo box with anti-slip surface. Heights: 20", 24", 30".',
    benefits: ['Safe landing', 'Multiple heights', 'Durable foam'],
    tier: 'essential',
    affiliateNote: 'Best for: Plyometric training',
    affiliateUrl: null
  },
  // Recovery
  {
    id: 12,
    name: 'Foam Roller Set',
    category: 'recovery',
    price: '$35',
    rating: 4.8,
    reviews: 6800,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
    description: 'High-density foam roller with textured surface. Includes massage ball and stick.',
    benefits: ['Deep tissue relief', 'Multiple densities', 'Complete set'],
    tier: 'essential',
    affiliateNote: 'Best for: Muscle recovery',
    affiliateUrl: null
  },
  {
    id: 13,
    name: 'Massage Gun',
    category: 'recovery',
    price: '$149',
    rating: 4.7,
    reviews: 4200,
    image: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=400&h=300&fit=crop',
    description: 'Quiet percussion massager with 6 heads and 5 speeds. 6-hour battery life.',
    benefits: ['Deep percussion', 'Quiet operation', 'Long battery'],
    tier: 'advanced',
    affiliateNote: 'Best for: Professional recovery',
    affiliateUrl: null
  },
  {
    id: 14,
    name: 'Yoga Mat (Extra Thick)',
    category: 'recovery',
    price: '$45',
    rating: 4.6,
    reviews: 3900,
    image: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=400&h=300&fit=crop',
    description: '6mm thick non-slip yoga mat. Perfect for stretching, mobility, and recovery.',
    benefits: ['Extra cushioning', 'Non-slip surface', 'Easy to clean'],
    tier: 'essential',
    affiliateNote: 'Best for: Stretching & mobility',
    affiliateUrl: null
  },
  {
    id: 15,
    name: 'Compression Sleeves (Pair)',
    category: 'recovery',
    price: '$28',
    rating: 4.5,
    reviews: 2100,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
    description: 'Graduated compression leg sleeves for recovery and performance.',
    benefits: ['Improves circulation', 'Reduces soreness', 'Comfortable fit'],
    tier: 'starter',
    affiliateNote: 'Best for: Post-workout recovery',
    affiliateUrl: null
  },
];

const tierColors = {
  starter: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  essential: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  advanced: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
};

const EquipmentPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTier, setActiveTier] = useState('all');

  const filteredEquipment = EQUIPMENT.filter(item => {
    const categoryMatch = activeCategory === 'all' || item.category === activeCategory;
    const tierMatch = activeTier === 'all' || item.tier === activeTier;
    return categoryMatch && tierMatch;
  });

  const handleViewProduct = (item) => {
    if (item.affiliateUrl) {
      // Open affiliate link in new tab
      window.open(item.affiliateUrl, '_blank', 'noopener,noreferrer');
    } else {
      // No affiliate link yet - show coming soon message
      toast.info(
        <div>
          <p className="font-medium">Coming Soon</p>
          <p className="text-sm text-zinc-400">Affiliate link for {item.name} will be available soon!</p>
        </div>
      );
    }
  };

  return (
    <DashboardLayout title="Equipment">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            Recommended Gear
          </h1>
          <p className="text-aura-text-muted mt-2">
            Quality equipment to support your training journey
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                ${activeCategory === cat.id 
                  ? 'bg-aura-primary text-white' 
                  : 'bg-white/5 text-aura-text-muted hover:bg-white/10 hover:text-white'
                }
              `}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.name}
            </button>
          ))}
        </div>

        {/* Tier Filter */}
        <div className="flex gap-2">
          <span className="text-xs text-aura-text-muted uppercase tracking-wider self-center mr-2">Level:</span>
          {['all', 'starter', 'essential', 'advanced'].map(tier => (
            <button
              key={tier}
              onClick={() => setActiveTier(tier)}
              className={`
                px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize
                ${activeTier === tier 
                  ? tier === 'all' ? 'bg-white/20 text-white' : `${tierColors[tier]?.bg} ${tierColors[tier]?.text}`
                  : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                }
              `}
              data-testid={`tier-${tier}`}
            >
              {tier === 'all' ? 'All Levels' : tier}
            </button>
          ))}
        </div>

        {/* Equipment Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEquipment.map(item => (
            <Card 
              key={item.id} 
              className="bg-aura-surface border-white/10 overflow-hidden hover:border-white/20 transition-all group"
              data-testid={`equipment-${item.id}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop';
                  }}
                />
                <div className="absolute top-3 left-3">
                  <Badge className={`${tierColors[item.tier]?.bg} ${tierColors[item.tier]?.text} ${tierColors[item.tier]?.border} border capitalize`}>
                    {item.tier}
                  </Badge>
                </div>
                <div className="absolute top-3 right-3 bg-black/70 px-2 py-1 rounded-full">
                  <span className="font-heading font-bold text-white">{item.price}</span>
                </div>
              </div>
              
              <CardContent className="p-4 space-y-3">
                {/* Title & Rating */}
                <div>
                  <h3 className="font-heading font-bold text-white text-lg leading-tight">
                    {item.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white text-sm ml-1">{item.rating}</span>
                    </div>
                    <span className="text-aura-text-muted text-sm">({item.reviews.toLocaleString()} reviews)</span>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-aura-text-muted text-sm line-clamp-2">
                  {item.description}
                </p>
                
                {/* Benefits */}
                <div className="flex flex-wrap gap-2">
                  {item.benefits.slice(0, 3).map((benefit, idx) => (
                    <span 
                      key={idx}
                      className="text-xs bg-white/5 text-aura-text-muted px-2 py-1 rounded-full"
                    >
                      {benefit}
                    </span>
                  ))}
                </div>
                
                {/* Affiliate Note */}
                <p className="text-xs text-aura-primary">{item.affiliateNote}</p>
                
                {/* CTA Button */}
                <Button 
                  onClick={() => handleViewProduct(item)}
                  className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white"
                  data-testid={`view-${item.id}`}
                >
                  View Product
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredEquipment.length === 0 && (
          <div className="text-center py-12">
            <ShoppingBag className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
            <p className="text-aura-text-muted">No equipment found for this filter</p>
          </div>
        )}

        {/* Starter Pack Suggestion */}
        <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-aura-primary" />
                  <h3 className="font-heading font-bold text-white uppercase">Starter Pack Recommendation</h3>
                </div>
                <p className="text-aura-text-muted text-sm max-w-xl">
                  New to training? Start with the essentials: Resistance Bands ($35) + Agility Ladder ($22) + Foam Roller Set ($35) = <span className="text-white font-bold">$92 total</span>
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-aura-primary text-aura-primary hover:bg-aura-primary hover:text-white whitespace-nowrap"
                onClick={() => {
                  setActiveCategory('all');
                  setActiveTier('starter');
                  toast.success('Showing starter-level equipment');
                }}
              >
                View Starter Gear
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className="text-xs text-aura-text-subtle text-center">
          Aura may earn a commission from qualifying purchases. Prices and availability are subject to change.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default EquipmentPage;
